<template>
  <div>
    <!-- Вкладки -->
    <ul class="nav nav-tabs mb-2">
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'all' }" href="#" @click.prevent="setTab('all')">
          Накладные-требования
          <span class="badge badge-info ml-1" v-if="allCount > 0">{{ allCount }}</span>
        </a>
      </li>
      <li class="nav-item">
        <a class="nav-link" :class="{ active: activeTab === 'pending' }" href="#" @click.prevent="setTab('pending')">
          Заявки
          <span class="badge badge-warning ml-1" v-if="pendingCount > 0">{{ pendingCount }}</span>
        </a>
      </li>
    </ul>

    <DBGrid :store="store" :config="config" ref="grid" />
  </div>
</template>

<script lang="ts">
import dayjs from 'dayjs';
import { defineComponent, computed, ref, watch, onMounted, onUnmounted, nextTick } from 'vue';
import InvoiceRequirementsListController from './ListController';
import { setCaption, pageTitle } from '@/layouts/layouts';
import DBStore from '@/core/db_store';
import Bus from '@/core/bus';
import stateStore from '@/store';

export default defineComponent({
  props: {
    panel: { type: Object, default: () => ({}) },
    store: { type: Object, default: () => ({}) },
    config: { type: Object, default: () => ({}) }
  },
  setup(props) {
    const grid = ref(null);
    const activeTab = ref('all');

    // === Проверка роли пользователя ===
    const user = stateStore.state.user;
    const isFullAccess = user.roles?.includes('admin') || user.roles?.includes('chief_medical') || user.roles?.includes('warehouse');
    const userConsigneeId = user.consignee_id || null;
    
    // Начальные фильтры
    const initialFilters: any[] = [
      {
        where: `NOT (
          COALESCE("invoice_requirements"."status", '') = 'requested_by_request'
          OR ("invoice_requirements"."status" = 'approved' AND COALESCE("invoice_requirements"."is_deficit", false) = true)
        )`,
        _tabFilter: true
      }
    ];

    // Подотчётник (warehouse2) видит только свои накладные-требования
    if (!isFullAccess && userConsigneeId) {
      initialFilters.push({
        where: `("invoice_requirements"."requested_by_id" = '${userConsigneeId}' OR "invoice_requirements"."department_id" = '${userConsigneeId}')`,
        _consigneeFilter: true
      });
    }
    
    const controller = new InvoiceRequirementsListController('invoice_requirements', {
      filters: initialFilters
    }, grid);
    const setup = controller.setup();

    // Фильтр подразделения (для подотчётника)
    const consigneeFilter = (!isFullAccess && userConsigneeId) ? {
      where: `("invoice_requirements"."requested_by_id" = '${userConsigneeId}' OR "invoice_requirements"."department_id" = '${userConsigneeId}')`,
      _consigneeFilter: true
    } : null;

    const setTab = async (tab: string) => {
      activeTab.value = tab;
      controller.activeTab = tab;

      // Убираем старые табовые фильтры, но сохраняем фильтр подразделения
      const currentFilters = Array.isArray(controller.store.fetchOptions.filters)
        ? controller.store.fetchOptions.filters
        : [];
      controller.store.fetchOptions.filters = currentFilters.filter(
        (f: any) => !f._tabFilter
      );

      // Добавляем фильтр подразделения, если его нет
      if (consigneeFilter) {
        const hasConsigneeFilter = controller.store.fetchOptions.filters.some((f: any) => f._consigneeFilter);
        if (!hasConsigneeFilter) {
          controller.store.fetchOptions.filters.push(consigneeFilter as any);
        }
      }

      if (tab === 'pending') {
        // Заявки: затребовано по заявке (плюс legacy-записи)
        controller.store.fetchOptions.filters.push({
          where: `(
            "invoice_requirements"."status" = 'requested_by_request'
            OR ("invoice_requirements"."status" = 'approved' AND COALESCE("invoice_requirements"."is_deficit", false) = true)
          )`,
          _tabFilter: true
        });
      } else {
        // Накладные-требования: обычные документы (включая «затребовано» и «отпущено»)
        controller.store.fetchOptions.filters.push({
          where: `NOT (
            COALESCE("invoice_requirements"."status", '') = 'requested_by_request'
            OR ("invoice_requirements"."status" = 'approved' AND COALESCE("invoice_requirements"."is_deficit", false) = true)
          )`,
          _tabFilter: true
        } as any);
      }

      controller.store.fetchOptions.page = 0;
      await controller.store.fetchData();

      // Обновляем кнопки панели (печать только на вкладке «Заявки»)
      controller.setButtons();
    };

    // Отдельные store для подсчёта записей на вкладках
    const countStoreAll = new DBStore('invoice_requirements');
    const countStorePending = new DBStore('invoice_requirements');

    // Счётчики из store (как в накладных)
    const allCount = computed(() => countStoreAll.data.rowCount || countStoreAll.data.rows?.length || 0);
    const pendingCount = computed(() => countStorePending.data.rowCount || countStorePending.data.rows?.length || 0);

    const updateCounts = async () => {
      // Базовые фильтры для подсчёта
      const baseCountFilters: any[] = [];
      if (consigneeFilter) {
        baseCountFilters.push(consigneeFilter);
      }

      // Подсчёт «Накладные-требования»
      countStoreAll.fetchOptions.filters = [
        ...baseCountFilters,
        {
          where: `NOT (
            COALESCE("invoice_requirements"."status", '') = 'requested_by_request'
            OR ("invoice_requirements"."status" = 'approved' AND COALESCE("invoice_requirements"."is_deficit", false) = true)
          )`
        } as any
      ];
      countStoreAll.fetchOptions.limit = 1;

      // Подсчёт «Заявки»
      countStorePending.fetchOptions.filters = [
        ...baseCountFilters,
        {
          where: `(
            "invoice_requirements"."status" = 'requested_by_request'
            OR ("invoice_requirements"."status" = 'approved' AND COALESCE("invoice_requirements"."is_deficit", false) = true)
          )`
        } as any
      ];
      countStorePending.fetchOptions.limit = 1;

      await Promise.all([
        countStoreAll.fetchData(),
        countStorePending.fetchData()
      ]);
    };

    // Цветовое выделение строк с учётом просрочки
    const config = computed(() => ({
      ...setup.config,
      ...props.config,
      styleTr: (data: any) => {
        const result: any = {};
        const status = (data._status || data.status || '').toString().trim().toLowerCase();
        const isDeficit =
          data.is_deficit === true ||
          data.is_deficit === 'true' ||
          status === 'deficit' ||
          status === 'requested_by_request';
        if (status === 'issued') {
          result['background-color'] = '#d4edda'; // зелёный — отпущено
        } else if (status === 'requested' || (status === 'approved' && !isDeficit)) {
          // Градиент по просрочке
          const dateStr = data.approval_date || data.created_at;
          const days = dateStr ? dayjs().diff(dayjs(dateStr), 'day') : 0;
          if (days > 14) {
            result['background-color'] = '#f8d7da'; // красный — критично
          } else if (days > 7) {
            result['background-color'] = '#ffe0b2'; // оранжевый
          } else if (days > 3) {
            result['background-color'] = '#fff3cd'; // жёлтый
          } else {
            result['background-color'] = '#cce5ff'; // голубой — свежая
          }
        } else if (isDeficit) {
          result['background-color'] = '#fff3cd'; // жёлтый — заявка на дефицит
        }
        return result;
      }
    }));

    const refreshData = async (saveData: any) => {
      if (saveData?.table === 'invoice_requirements' || !saveData?.table) {
        await setTab(activeTab.value);
        updateCounts();
      }
    };

    watch(grid, (g: any) => {
      if (g && g.controller) {
        controller.setButtons();

        // Кастомный render для approval_date — показываем бейдж «X дн.» для заявок
        const approvalField = g.controller.store.state.fields?.approval_date;
        if (approvalField) {
          if (!approvalField.config) approvalField.config = {};
          approvalField.config.render = (value: any, row: any) => {
            if (!value || value === '0000-00-00') return '';
            const formatted = dayjs(value).format('DD.MM.YYYY');
            const status = (row._status || row.status || '').toString().trim().toLowerCase();
            if (status === 'requested' || status === 'requested_by_request' || status === 'approved') {
              const days = dayjs().diff(dayjs(value), 'day');
              let cls = 'badge-info';
              let label = `${days} дн.`;
              if (days > 14) cls = 'badge-danger';
              else if (days > 7) cls = 'badge-warning';
              else if (days > 3) cls = 'badge-secondary';
              return `${formatted} <span class="badge ${cls}" style="font-size:10px;margin-left:4px;vertical-align:middle">${label}</span>`;
            }
            return formatted;
          };
        }

        // Аналогично для status — показываем просрочку текстом
        const statusField = g.controller.store.state.fields?.status;
        if (statusField) {
          if (!statusField.config) statusField.config = {};
          const origRender = statusField.config.render;
          statusField.config.render = (value: any, row: any) => {
            // Определяем отображаемый статус
            let display = row._status;
            if (!display && statusField.type?.enum) {
              const item = statusField.type.enum.find((e: any) => e.id === value);
              display = item ? item.name : value;
            }
            if (!display) display = value || '';

            const st = (value || '').toString().trim().toLowerCase();
            if (st === 'requested' || st === 'requested_by_request' || st === 'approved') {
              const dateStr = row.approval_date || row.created_at;
              if (dateStr) {
                const days = dayjs().diff(dayjs(dateStr), 'day');
                let cls = 'badge-info';
                if (days > 14) cls = 'badge-danger';
                else if (days > 7) cls = 'badge-warning';
                else if (days > 3) cls = 'badge-secondary';
                return `${display} <span class="badge ${cls}" style="font-size:10px;margin-left:3px">${days} дн.</span>`;
              }
            }
            return display;
          };
        }
      }
    }, { immediate: true });

    onMounted(async () => {
      setCaption('');
      pageTitle.value = null;

      // Загружаем с фильтром для текущей вкладки
      await setTab('all');
      updateCounts();

      Bus.$on('refresh-data', refreshData);
      Bus.$on('save-data', refreshData);
      Bus.$on('form-saved', refreshData);
    });

    onUnmounted(() => {
      Bus.$off('refresh-data', refreshData);
      Bus.$off('save-data', refreshData);
      Bus.$off('form-saved', refreshData);
    });

    return {
      ...setup,
      config,
      grid,
      activeTab,
      setTab,
      pendingCount,
      allCount
    };
  }
});
</script>
